import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import type { FC } from 'react';
import React, { use, useRef } from 'react';

import { A11ySkipToContent } from 'src/components/common/a11y-skip-to-content';
import { WebsiteLink } from 'src/components/common/website-link';
import { HeaderActions } from 'src/components/layout/redesign/header-desktop/header-action';
import HeaderDesktopMenu from 'src/components/layout/redesign/header-desktop/header-menu';
import { CLUB_NAME_LENGTH_TO_REDUCE_SIZE } from 'src/components/layout/redesign/header-desktop/heder-desktop-helper';
import type { HeaderButtonProps } from 'src/components/layout/redesign/header-mobile/header-button';
import HeaderButton from 'src/components/layout/redesign/header-mobile/header-button';
import type { BaseHeaderContent } from 'src/components/layout/redesign/header-mobile/header-menu';
import { HeaderLogo } from 'src/components/layout/redesign/header-shared/header-logo';
import LanguageSelection from 'src/components/layout/redesign/header-shared/language-selection';
import { getClubInformation } from 'src/content/club-look-ups-by-id';
import { SiteContext } from 'src/context/site-context';
import useWindowSize from 'src/custom-hooks/use-window-size';
import { BuildType } from 'src/enums/build/build-type';
import { amplitudeLinkTrackEvent } from 'src/helpers/amplitude-event-track';
import { clubNameDependingOnBuildTypeAndLanguage } from 'src/helpers/club-helper';
import { isClubLinkCurrentPage, isGlobalLinkCurrentPage } from 'src/helpers/navigation-helper';

import * as styles from './header-navigation.module.scss';

export type HeaderNavigationProps = {
  logoClick: () => void;
  isUserTabbing: boolean;
  headerContent: BaseHeaderContent;
  primaryButton?: HeaderButtonProps;
  secondaryButton?: HeaderButtonProps;
  searchButton?: HeaderButtonProps;
  openSubMenu: (index: number) => void;
  setIsUserTabbing: (val: boolean) => void;
  displayedSubMenuIndex: number | null;
  canOpenSubMenu: boolean;
  setCanOpenSubMenu: (val: boolean) => void;
  onHover: () => void;
  onFinishMenuAction: () => void;
  forceDesktopHeader?: boolean;
};

const HeaderNavigation: FC<HeaderNavigationProps> = ({
  logoClick,
  isUserTabbing,
  headerContent,
  primaryButton,
  secondaryButton,
  searchButton,
  openSubMenu,
  setIsUserTabbing,
  displayedSubMenuIndex,
  canOpenSubMenu,
  setCanOpenSubMenu,
  onHover,
  onFinishMenuAction,
  forceDesktopHeader = false,
}) => {
  const { language, buildType, basePath, pageLevel, isGlobalPage, siteId, isMeridian } =
    use(SiteContext);
  const navigationRef = useRef<HTMLDivElement>(null);
  const headerButtonRef = useRef<HTMLAnchorElement | null>(null);
  const { isMobileHeader } = useWindowSize(navigationRef);

  const getPrimaryNavMarginClass = () => {
    switch (buildType) {
      case BuildType.MERIDIAN:
        return isGlobalPage ? styles.primaryNavMeridianGlobal : styles.primaryNavMeridianLocal;
      default:
        return isGlobalPage ? styles.primaryNavDefaultGlobal : styles.primaryNavDefaultLocal;
    }
  };

  const onPrimaryNavHover = (event: React.PointerEvent<HTMLLIElement>, index: number) => {
    if (!canOpenSubMenu) {
      return;
    }

    openSubMenu(index);
    event.stopPropagation();
  };

  const isLinkCurrentPage = (link: string) =>
    isGlobalPage
      ? isGlobalLinkCurrentPage(link, language, buildType, basePath)
      : isClubLinkCurrentPage(link, language, siteId, basePath);

  const onPrimaryNavClick = (link: string) => {
    setIsUserTabbing(false);

    if (!isLinkCurrentPage(link)) {
      setCanOpenSubMenu(false);
      onFinishMenuAction();
    }
  };

  const clubName = clubNameDependingOnBuildTypeAndLanguage(
    getClubInformation(siteId).clubName,
    buildType,
    language
  );

  const clubNameStyle = {
    '--club-name-size': isMeridian
      ? '1.875rem'
      : clubName.length > CLUB_NAME_LENGTH_TO_REDUCE_SIZE
        ? '2rem'
        : '2.5rem',
  } as React.CSSProperties;

  return (
    <div ref={navigationRef} className={styles.navigationContainer}>
      <div className={styles.topRow}>
        <div
          className={classNames(styles.logo, isGlobalPage ? styles.logoGlobal : styles.logoLocal)}
          onPointerOver={onHover}
          style={{ position: 'relative' }}
        >
          <WebsiteLink to="/" goToGlobal handleClick={logoClick}>
            <HeaderLogo
              davidLloydSize="9.375rem"
              meridianSize="9.5rem"
              harbourSize="9.5rem"
              togetherFoundationSize="10rem"
            />
          </WebsiteLink>
          {!isGlobalPage && (
            <>
              <A11ySkipToContent />
              <p className={styles.clubName} style={clubNameStyle}>
                {clubName}
              </p>
            </>
          )}
        </div>
        <nav className={styles.secondaryNavContainer}>
          <ul className={styles.secondaryNavList}>
            {headerContent?.headerSecondaryUpperMenu?.map(
              ({ text, link, openInNewTab, goToGlobal }) => {
                const handleClick = () => {
                  onFinishMenuAction();
                  void amplitudeLinkTrackEvent({
                    buildType,
                    language,
                    linkName: capitalize(text),
                    linkPath: link,
                    componentName: 'Header Navigation',
                    pageLevel,
                    linkEventName: 'Link in Navigation',
                  });
                };

                return (
                  <li key={text}>
                    <WebsiteLink
                      to={link}
                      openInNewTab={!!openInNewTab}
                      handleClick={handleClick}
                      goToGlobal={goToGlobal ?? undefined}
                    >
                      <div
                        className={classNames(
                          styles.secondaryNavLink,
                          isLinkCurrentPage(link) && styles.active,
                          styles.hover
                        )}
                        onPointerOver={onHover}
                      >
                        {text}
                      </div>
                    </WebsiteLink>
                  </li>
                );
              }
            )}
            <LanguageSelection text={headerContent?.headerOtherText?.selectLanguageText} />
            {/* If the secondary button is disabled, the search button is rendered by a different component instead */}
            {secondaryButton && searchButton && (
              <HeaderButton ref={headerButtonRef} {...searchButton} className={styles.noWrap} />
            )}
          </ul>
        </nav>
      </div>
      <div className={styles.topRow}>
        <nav
          className={classNames(
            styles.primaryNavContainer,
            isGlobalPage && styles.primaryNavContainerGlobal,
            getPrimaryNavMarginClass()
          )}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget) && isUserTabbing) {
              onFinishMenuAction();
            }
          }}
        >
          <ul className={styles.primaryNavList}>
            {headerContent?.headerMenuItems?.map((item, index) => {
              const handleClick = () => {
                onFinishMenuAction();
                void amplitudeLinkTrackEvent({
                  buildType,
                  language,
                  linkName: capitalize(item.name),
                  linkPath: item.link,
                  componentName: 'Header Navigation',
                  pageLevel,
                  linkEventName: 'Link in Navigation',
                });
              };
              return (
                <li key={item.name} onPointerOver={(event) => onPrimaryNavHover(event, index)}>
                  {item.link ? (
                    <WebsiteLink
                      to={item.link}
                      className={styles.linkRowCentered}
                      onFocus={() => {
                        openSubMenu(index);
                        setIsUserTabbing(true);
                      }}
                      handleClick={handleClick}
                    >
                      <div
                        data-testid={item.name}
                        className={classNames(
                          styles.primaryNavLink,
                          isLinkCurrentPage(item.link) && styles.active,
                          displayedSubMenuIndex === index && styles.selected
                        )}
                        onClick={() => onPrimaryNavClick(item.link!)}
                      >
                        {item.name}
                      </div>
                    </WebsiteLink>
                  ) : (
                    <div
                      data-testid={item.name}
                      className={classNames(
                        styles.primaryNavLink,
                        styles.primaryNavLinkStatic,
                        displayedSubMenuIndex === index && styles.selected
                      )}
                      tabIndex={0}
                      onFocus={() => {
                        openSubMenu(index);
                        setIsUserTabbing(true);
                      }}
                      onClick={() => {
                        setIsUserTabbing(false);
                      }}
                    >
                      {item.name}
                    </div>
                  )}
                  {(forceDesktopHeader || !isMobileHeader) && (
                    <HeaderDesktopMenu
                      {...item}
                      overviewText={headerContent.headerOtherText?.overviewText ?? ''}
                      visible={displayedSubMenuIndex === index}
                      onFinish={onFinishMenuAction}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
        <div
          className={classNames(
            styles.inlineActionsWrapper,
            isGlobalPage && styles.inlineActionsWrapperGlobal,
            isMeridian && isGlobalPage && styles.inlineActionsMeridianGlobal
          )}
        >
          <HeaderActions
            primaryButton={primaryButton}
            secondaryButton={secondaryButton}
            searchButton={searchButton}
            onHover={onHover}
            linkDropdownCta={headerContent?.linkDropdownCta}
          />
        </div>
      </div>
    </div>
  );
};

export { HeaderNavigation };
